import { CopyButton } from '@/components/ui/copy-button'
import { FormConnectionTabs } from '@/pages/wordpress-setup/form-connection-tabs'

const API_BASE = 'https://api.one-email.com/v1/subscribe'

interface Props {
  audienceId: string
}

export function SubscribeFormInline({ audienceId }: Props): React.JSX.Element {
  const endpointUrl = audienceId ? `${API_BASE}/${audienceId}` : `${API_BASE}/{audienceId}`

  return (
    <div className="space-y-5">
      {/* Section 1: Endpoint URL */}
      <div>
        <h4 className="text-sm font-medium text-text-primary">Your Subscribe Endpoint</h4>
        <p className="mt-0.5 text-xs text-text-tertiary">
          {audienceId
            ? 'This is your subscribe endpoint. Point any form to this URL.'
            : 'Select an audience in Step 4 first — the endpoint will update with your audience ID.'}
        </p>
        <div className="mt-2 flex items-center gap-2 rounded-lg bg-surface-1 p-3">
          <code className="flex-1 font-mono text-sm text-text-primary select-all break-all">
            {endpointUrl}
          </code>
          {audienceId && <CopyButton value={endpointUrl} />}
        </div>
        {!audienceId && (
          <p className="mt-2 text-xs font-medium text-amber-600">
            Go back to Step 4 and select an audience to get your endpoint URL.
          </p>
        )}
      </div>

      {/* Section 2: Required Fields */}
      <div>
        <h4 className="text-sm font-medium text-text-primary">Required Fields</h4>
        <div className="mt-2 overflow-hidden rounded-lg border border-surface-3">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-surface-1 text-left">
                <th className="px-3 py-2 text-xs font-semibold text-text-secondary">Field</th>
                <th className="px-3 py-2 text-xs font-semibold text-text-secondary">Type</th>
                <th className="px-3 py-2 text-xs font-semibold text-text-secondary">Required</th>
                <th className="px-3 py-2 text-xs font-semibold text-text-secondary">Description</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-surface-3">
                <td className="px-3 py-2 font-mono text-xs">email</td>
                <td className="px-3 py-2 text-xs text-text-secondary">text / email</td>
                <td className="px-3 py-2 text-xs text-text-secondary">Yes</td>
                <td className="px-3 py-2 text-xs text-text-tertiary">
                  The subscriber&apos;s email address
                </td>
              </tr>
              <tr className="border-t border-surface-3">
                <td className="px-3 py-2 font-mono text-xs">website</td>
                <td className="px-3 py-2 text-xs text-text-secondary">text (hidden)</td>
                <td className="px-3 py-2 text-xs text-text-secondary">No</td>
                <td className="px-3 py-2 text-xs text-text-tertiary">
                  Honeypot &mdash; add a hidden field to catch bots
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Section 3: Tabbed connection instructions */}
      <div>
        <h4 className="text-sm font-medium text-text-primary">How to Connect Your Form</h4>
        <p className="mt-0.5 mb-2 text-xs text-text-tertiary">
          Choose the platform you use. Any tool that can POST to a URL will work.
        </p>
        <FormConnectionTabs endpointUrl={endpointUrl} />
      </div>

      {/* Section 4: What Happens After Submit */}
      <div className="rounded-lg bg-surface-1 px-4 py-3">
        <h4 className="text-xs font-semibold text-text-secondary">What Happens After Submit</h4>
        <ul className="mt-1.5 space-y-1">
          <li className="text-xs text-text-tertiary">
            &bull; Subscriber receives a confirmation email (double opt-in)
          </li>
          <li className="text-xs text-text-tertiary">
            &bull; Once they confirm, they appear in your audience
          </li>
          <li className="text-xs text-text-tertiary">
            &bull; You&apos;ll see them in your One Email dashboard &rarr; Audiences
          </li>
          <li className="text-xs text-text-tertiary">
            &bull; The endpoint returns JSON &mdash;{' '}
            <code className="rounded bg-surface-2 px-1 py-0.5 font-mono text-[11px]">
              {'{ "message": "Check your inbox..." }'}
            </code>{' '}
            on success
          </li>
        </ul>
      </div>

    </div>
  )
}
